import { z } from "zod";
import { ProjectTask, projectTaskSchema } from "tekkr-common/dist/model/project/project";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { Button } from "../../../components/ui/button";
import { ChevronDownIcon, EllipsisIcon, ListCheckIcon, PaperclipIcon, Trash2Icon, Undo2Icon } from "lucide-react";
import { PopoverTrigger } from "../../../components/ui/popover";
import { AiTask, AiTaskButton } from "./ai-task-button";
import React, { useEffect, useMemo, useState } from "react";
import { cn } from "../../../lib/utils";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import AnimateHeight from "react-animate-height";
import { OrgUser } from "../../../data/service/tekkr-service-types";
import { AvatarForUser } from "../../../components/shared/org/avatar-for-user";
import { UserDropdown } from "../../../components/shared/org/user-dropdown";
import { useProjectAttachments } from "../../../data/project-attachments";
import { useOrg } from "../../../auth/org-provider";
import { useUpdateProjectTaskMutation } from "../../../data/mutations/update-project-task";
import { useDeleteProjectTaskMutation } from "../../../data/mutations/delete-project-task";
import { CreateAttachmentDialog } from "./create-attachment-dialog";
import { DialogTrigger } from "../../../components/ui/dialog";
import { AttachmentListDialog } from "./attachment-list-dialog";
import { useConfirmDialog } from "../../../components/modals/confirmation-dialog-provider";
import { TaskStatusSelector } from "../../../components/shared/project/task-status-selector";

type Task = z.infer<typeof projectTaskSchema>;
type TaskState = ProjectTask["status"];
type TaskUpdate = { status: TaskState } | { assignee: string | null };

export function TaskList(props: { tasks: Task[] }) {
    const { tasks } = props;
    return (
        <Table>
            <TableHeader>
                <TableRow className={"pointer-events-none"}>
                    <TableHead className={"flex flex-row items-center gap-1 p-2"}>
                        <ListCheckIcon className={"h-4 w-4"} />
                        Tasks
                    </TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {tasks.map((t, i) => (
                    <TaskRow index={i} t={t} key={t.id} />
                ))}
            </TableBody>
        </Table>
    );
}

function TaskAssigneeView({
    assignee,
    users,
    onAssigned,
}: {
    assignee?: string | null;
    users: OrgUser[];
    onAssigned: (userId: string | null) => void;
}) {
    const user = assignee ? users.find((u) => u.id === assignee) : null;

    if (user) {
        return (
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant={"ghost"}
                        size={"sm"}
                        className="flex h-8 items-center gap-1 hover:opacity-80"
                        title={user.name}
                    >
                        <AvatarForUser user={user} className={"h-5 w-5"} fallback={"icon"} />
                        <span className={"max-w-16 truncate text-xs font-medium"}>{user.name.split(" ")[0]}</span>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                    <DropdownMenuItem onClick={() => onAssigned(null)}>
                        <Undo2Icon className={"me-2 h-4 w-4"} />
                        Unassign
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        );
    }

    return (
        <UserDropdown users={users} onSelect={(userId) => onAssigned(userId)}>
            <PopoverTrigger asChild>
                <Button size={"sm"} variant={"outline"} className="h-8">
                    Assign <ChevronDownIcon className={"ms-1 h-3.5 w-3.5"} />
                </Button>
            </PopoverTrigger>
        </UserDropdown>
    );
}

function TaskRowMenu(props: TaskRowProps) {
    const { t } = props;
    const [showCreateAttachmentDialog, setShowCreateAttachmentDialog] = useState(false);
    const deleteTaskMutation = useDeleteProjectTaskMutation();
    const confirm = useConfirmDialog();

    const handleRemoveTask = confirm(
        {
            title: "Remove Task",
            body: (
                <div className={"flex flex-col gap-3 pb-3"}>
                    <div>
                        Are you sure you want to remove <strong className={"font-semibold"}>"{t.title}"</strong>?
                    </div>
                    <div className={"text-sm text-muted-foreground"}>This action cannot be undone.</div>
                </div>
            ),
            confirmButtonText: "Remove",
            confirmButtonVariant: "destructive",
        },
        () => {
            deleteTaskMutation.mutate({ taskId: t.id });
        }
    );

    return (
        <DropdownMenu>
            <CreateAttachmentDialog
                open={showCreateAttachmentDialog}
                onOpenChange={setShowCreateAttachmentDialog}
                target={["task", t.id]}
                title="Add Task Attachment"
            />
            <DropdownMenuTrigger asChild>
                <Button size={"sm"} variant={"ghost"} className="h-8">
                    <EllipsisIcon className={"h-4 w-4"} />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-48" align="end">
                <DropdownMenuItem onClick={() => setShowCreateAttachmentDialog(true)}>
                    <PaperclipIcon className={"me-2 h-4 w-4"} />
                    Attach file or link
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleRemoveTask} className="text-destructive focus:text-destructive">
                    <Trash2Icon className={"me-2 h-4 w-4"} />
                    Remove task
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function TaskRowAttachments(props: TaskRowProps) {
    const { t } = props;
    const attachments = useProjectAttachments();
    const taskAttachments = useMemo(() => {
        return attachments.getAttachments(["task", t.id]);
    }, [attachments, t.id]);

    if (taskAttachments.length === 0) {
        return null;
    }

    return (
        <AttachmentListDialog scope={["task", t.id]} subtitle={`Task: ${t.title}`}>
            <DialogTrigger>
                <div
                    className={
                        "flex h-8 cursor-pointer flex-row items-center gap-1 rounded-md bg-accent px-2 text-sm font-medium"
                    }
                >
                    <PaperclipIcon className={"h-4 w-4"} />
                    <div className={"whitespace-nowrap"}>
                        {taskAttachments.length} attachment{taskAttachments.length === 1 ? "" : "s"}
                    </div>
                </div>
            </DialogTrigger>
        </AttachmentListDialog>
    );
}

function TaskRowActions(
    props: TaskRowProps & {
        taskState: TaskState;
        assignedUserId?: string | null;
        onTaskUpdate: (update: TaskUpdate) => void;
    }
) {
    const org = useOrg();
    const { taskState, t, assignedUserId, onTaskUpdate } = props;
    const { refetch: refetchAttachments } = useProjectAttachments();

    if (isAiTask(t)) {
        const onTaskComplete = () => {
            onTaskUpdate({ status: "done" });
            void refetchAttachments();
        };
        return (
            <>
                {taskState !== "done" && <AiTaskButton task={t} onTaskComplete={onTaskComplete} />}
                <TaskStatusSelector
                    status={taskState}
                    onStatusChange={(status) => onTaskUpdate({ status })}
                    availableStatuses={[null, "done"]}
                />
            </>
        );
    }

    return (
        <>
            {(taskState !== "done" || assignedUserId) && (
                <TaskAssigneeView
                    assignee={assignedUserId}
                    users={org.users}
                    onAssigned={(userId) => onTaskUpdate({ assignee: userId })}
                />
            )}
            <TaskStatusSelector status={taskState} onStatusChange={(status) => onTaskUpdate({ status })} />
        </>
    );
}

type TaskRowProps = {
    t: Task;
    index: number;
};

function TaskRow(props: TaskRowProps) {
    const { t, index } = props;

    const [taskStatus, setTaskStatusState] = React.useState(t.status);
    const [assignedUserId, setAssignedUserId] = React.useState(t.assignee);

    useEffect(() => {
        setTaskStatusState(t.status);
        setAssignedUserId(t.assignee);
    }, [t.status, t.assignee]);

    const updateTaskMutation = useUpdateProjectTaskMutation();
    function updateTask(update: TaskUpdate) {
        if ("status" in update) {
            setTaskStatusState(update.status);
        }
        if ("assignee" in update) {
            setAssignedUserId(update.assignee);
        }
        updateTaskMutation.mutate({ taskId: t.id, update });
    }

    return (
        <TableRow key={t.id} className="group">
            <TableCell className="p-2 font-medium">
                <TaskTitle
                    title={t.title}
                    description={t.description}
                    position={index + 1}
                    taskCompleted={taskStatus === "done"}
                />
            </TableCell>
            <TableCell className="flex shrink-0 flex-row items-center justify-end gap-2 p-2">
                <TaskRowAttachments {...props} />
                <TaskRowActions
                    {...props}
                    taskState={taskStatus}
                    assignedUserId={assignedUserId}
                    onTaskUpdate={updateTask}
                />
                <TaskRowMenu {...props} />
            </TableCell>
        </TableRow>
    );
}

export function TaskTitle(props: { title: string; description: string; position: number; taskCompleted: boolean }) {
    const [expanded, setExpanded] = useState(false);
    return (
        <div className={"flex flex-col"}>
            <div className={"group cursor-pointer"} onClick={() => setExpanded(!expanded)}>
                <span className={"pe-2 opacity-45"}>{props.position}.</span>
                <span
                    className={cn("group-hover:underline", props.taskCompleted && "text-muted-foreground line-through")}
                >
                    {props.title}
                </span>
            </div>
            <AnimateHeight height={expanded ? "auto" : 0} duration={300}>
                <div className={"mt-1 text-muted-foreground"}>{props.description}</div>
            </AnimateHeight>
        </div>
    );
}

function isAiTask(task: Task): task is AiTask {
    return task.execution.targetActor === "bot";
}
